<template>
    <div
        class="ds-week-cell"
        :class="{ 'ds-week-cell-day-state': mode === 'dayState' }"
        :style="mode === 'dayState' ? { backgroundColor: dayStateColor } : undefined"
        :data-schedule-date="date"
    >
        <div v-if="stripe" class="ds-week-cell-stripe" :class="STRIPE_CLASSES[stripe]" />

        <!-- RD-182: своя + внешняя — две колонки: собственная основная и внешняя на штриховке (магазин и план;
             подпись «другой маг.» из макета убрана по решению Романа 28.10.2026).
             Внизу часы и «из …» только своих смен; скрытые собственные доп. — лиловый счётчик. -->
        <template v-if="isMixed && externalView">
            <div class="ds-week-cell-body ds-week-cell-body-compact">
                <div class="ds-week-columns">
                    <div v-for="shift in mixedOwnShifts" :key="shift.key" class="ds-week-column" :class="{ 'ds-week-column-extra': shift.isExtra }">
                        <span class="ds-week-column-position">{{ shift.positionName }}</span>
                        <span class="ds-week-column-plan">{{ shift.planStart }}–{{ shift.planEnd }}<sup v-if="shift.planNextDay" class="ds-week-next-day">+1</sup></span>
                        <span class="ds-week-column-fact"><template v-if="shift.factStart || shift.factEnd"><span :class="{ 'ds-week-fact-time-violation': shift.startViolated }">{{ shift.factStart }}</span>–<span :class="{ 'ds-week-fact-time-violation': shift.endViolated }">{{ shift.factEnd }}</span><sup v-if="shift.factNextDay" class="ds-week-next-day">+1</sup></template></span>
                    </div>
                    <div class="ds-week-column ds-week-column-external" data-external-shift>
                        <span class="ds-week-column-external-name">{{ externalView.departmentName }}</span>
                        <span class="ds-week-column-plan"><template v-if="externalView.plan">{{ externalView.plan.start }}–{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-week-next-day">+{{ externalView.plan.nextDay }}</sup></template><template v-else>{{ externalView.planText }}</template></span>
                    </div>
                </div>
                <div class="ds-week-columns-total">
                    <span class="ds-week-columns-total-value" :class="{ 'ds-week-hours-value-muted': shiftViews.some(view => view.isInProgress) }">{{ totalFactHours }}</span>
                    <span v-if="showTotalPlanHours" class="ds-week-hours-plan">из {{ totalPlanHours }}</span>
                    <span v-if="mixedHiddenCount" class="ds-week-hidden-count">+{{ mixedHiddenCount }}</span>
                </div>
            </div>
        </template>

        <!-- Только внешняя: блок на штриховке с магазином и планом, без собственных часов. -->
        <div v-else-if="mode === 'external' && externalView" class="ds-week-cell-body ds-week-cell-body-external">
            <div class="ds-week-external" data-external-shift>
                <span class="ds-week-external-name">{{ externalView.departmentName }}</span>
                <span class="ds-week-external-time"><template v-if="externalView.plan">{{ externalView.plan.start }}–{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-week-next-day">+{{ externalView.plan.nextDay }}</sup></template><template v-else>{{ externalView.planText }}</template></span>
            </div>
        </div>

        <template v-else-if="mode === 'single' && singleShift">
            <div class="ds-week-cell-body">
                <div class="ds-week-shift-line">
                    <div class="ds-week-plan">
                        <span>{{ singleShift.planStart }}</span>
                        <span class="ds-week-time-end">{{ singleShift.planEnd }}<sup v-if="singleShift.planNextDay" class="ds-week-next-day">+1</sup></span>
                    </div>
                    <div class="ds-week-fact">
                        <span><span class="ds-week-fact-time" :class="{ 'ds-week-fact-time-violation': singleShift.startViolated }">{{ singleShift.factStart }}</span></span>
                        <span class="ds-week-time-end"><span class="ds-week-fact-time" :class="{ 'ds-week-fact-time-violation': singleShift.endViolated }">{{ singleShift.factEnd }}</span><sup v-if="singleShift.factNextDay" class="ds-week-next-day ds-week-next-day-fact">+1</sup></span>
                    </div>
                    <div class="ds-week-hours">
                        <span class="ds-week-hours-value" :class="{ 'ds-week-hours-value-muted': singleShift.isInProgress }">{{ singleShift.factHours }}</span>
                        <span v-if="singleShift.showPlanHours" class="ds-week-hours-plan">из {{ singleShift.planHours }}</span>
                    </div>
                </div>
                <div class="ds-week-chips">
                    <span v-for="chip in chips" :key="chip.text" class="ds-week-chip" :class="CHIP_CLASSES[chip.kind]">{{ chip.text }}</span>
                </div>
            </div>
        </template>

        <template v-else-if="mode === 'multi'">
            <div class="ds-week-cell-body ds-week-cell-body-compact">
                <div class="ds-week-columns">
                    <div v-for="shift in visibleShifts" :key="shift.key" class="ds-week-column" :class="{ 'ds-week-column-extra': shift.isExtra }">
                        <span class="ds-week-column-position">{{ shift.positionName }}</span>
                        <span class="ds-week-column-plan">{{ shift.planStart }}–{{ shift.planEnd }}<sup v-if="shift.planNextDay" class="ds-week-next-day">+1</sup></span>
                        <span class="ds-week-column-fact"><template v-if="shift.factStart || shift.factEnd"><span :class="{ 'ds-week-fact-time-violation': shift.startViolated }">{{ shift.factStart }}</span>–<span :class="{ 'ds-week-fact-time-violation': shift.endViolated }">{{ shift.factEnd }}</span><sup v-if="shift.factNextDay" class="ds-week-next-day">+1</sup></template></span>
                    </div>
                </div>
                <div class="ds-week-columns-total">
                    <span class="ds-week-columns-total-value">{{ totalFactHours }}</span>
                    <span class="ds-week-hours-plan">из {{ totalPlanHours }}</span>
                    <span v-if="hiddenShiftsCount" class="ds-week-hidden-count">+{{ hiddenShiftsCount }}</span>
                </div>
            </div>
        </template>

        <template v-else-if="mode === 'dayState'">
            <div v-if="dayStateShiftView" class="ds-week-cell-body ds-week-cell-body-compact">
                <!-- RD-185: часы КС — справа в строке подписи КС, отдельно от часов рабочей смены. -->
                <div v-if="dayStateDuration" class="ds-week-day-state-caption-line">
                    <span class="ds-week-day-state-caption">{{ dayStateName }}</span>
                    <span class="ds-week-day-state-hours" data-day-state-duration>{{ dayStateDuration }}</span>
                </div>
                <div v-else class="ds-week-day-state-caption">{{ dayStateName }}</div>
                <div class="ds-week-day-state-shift">
                    <div class="ds-week-column ds-week-column-extra">
                        <span class="ds-week-column-position">{{ dayStateShiftView.positionName }}</span>
                        <span class="ds-week-column-plan">{{ dayStateShiftView.planStart }}–{{ dayStateShiftView.planEnd }}<sup v-if="dayStateShiftView.planNextDay" class="ds-week-next-day">+1</sup></span>
                        <span class="ds-week-column-fact"><template v-if="dayStateShiftView.factStart || dayStateShiftView.factEnd"><span :class="{ 'ds-week-fact-time-violation': dayStateShiftView.startViolated }">{{ dayStateShiftView.factStart }}</span>–<span :class="{ 'ds-week-fact-time-violation': dayStateShiftView.endViolated }">{{ dayStateShiftView.factEnd }}</span><sup v-if="dayStateShiftView.factNextDay" class="ds-week-next-day">+1</sup></template></span>
                    </div>
                    <div class="ds-week-hours">
                        <span class="ds-week-hours-value" :class="{ 'ds-week-hours-value-muted': dayStateShiftView.isInProgress }">{{ dayStateShiftView.factHours }}</span>
                        <span v-if="dayStateShiftView.showPlanHours" class="ds-week-hours-plan">из {{ dayStateShiftView.planHours }}</span>
                    </div>
                    <!-- КС + своя + внешняя: внешняя колонкой на штриховке справа от своей смены и её часов. -->
                    <div v-if="externalView" class="ds-week-column ds-week-column-external" data-external-shift>
                        <span class="ds-week-column-external-name">{{ externalView.departmentName }}</span>
                        <span class="ds-week-column-plan"><template v-if="externalView.plan">{{ externalView.plan.start }}–{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-week-next-day">+{{ externalView.plan.nextDay }}</sup></template><template v-else>{{ externalView.planText }}</template></span>
                    </div>
                </div>
            </div>
            <div v-else-if="externalView" class="ds-week-cell-body ds-week-cell-body-compact">
                <!-- RD-185: часы КС — справа в строке подписи КС, отдельно от часов рабочей смены. -->
                <div v-if="dayStateDuration" class="ds-week-day-state-caption-line">
                    <span class="ds-week-day-state-caption">{{ dayStateName }}</span>
                    <span class="ds-week-day-state-hours" data-day-state-duration>{{ dayStateDuration }}</span>
                </div>
                <div v-else class="ds-week-day-state-caption">{{ dayStateName }}</div>
                <div class="ds-week-external-row" data-external-shift>
                    <span class="ds-week-external-row-name">{{ externalView.departmentName }}</span>
                    <span class="ds-week-external-row-time"><template v-if="externalView.plan">{{ externalView.plan.start }}–{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-week-next-day">+{{ externalView.plan.nextDay }}</sup></template><template v-else>{{ externalView.planText }}</template></span>
                </div>
            </div>
            <!-- RD-185: только КС — название и часы справа, как часы обычной смены. -->
            <div v-else-if="dayStateDuration" class="ds-week-cell-label ds-week-day-state-title ds-week-day-state-label">
                <span class="ds-week-day-state-label-name">{{ dayStateName }}</span>
                <span class="ds-week-hours-value" data-day-state-duration>{{ dayStateDuration }}</span>
            </div>
            <div v-else class="ds-week-cell-label ds-week-day-state-title">{{ dayStateName }}</div>
        </template>

        <div v-else-if="mode === 'dayoff'" class="ds-week-cell-label">{{ dayoffLabel }}</div>

        <div v-else-if="mode === 'add'" class="ds-week-cell-add">
            <icon width="16" height="16"><IconPlus /></icon>
        </div>

        <div v-if="isToday" class="ds-week-cell-today" aria-hidden="true" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import type { Schedule, Shift } from '@/types'
import IconPlus from '@/ui/icons/IconPlus.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import { formatDecimalHours } from '@/utils/duration'
import { buildExternalShiftView, ownRowShifts, rowExternalShifts } from '@/utils/otherDepartmentShifts'
import {
    DATE_FORMAT,
    describeShiftTiming,
    formatDayStateDuration,
    isDayStateDurationShown,
    pluralViolations,
    useWallClockNow,
} from './weekSchedule'

interface Props {
    schedule: Schedule
    date: string
}

type CellMode = 'blank' | 'add' | 'dayoff' | 'dayState' | 'single' | 'multi' | 'external'
type ChipKind = 'violation' | 'waiting'

// Полные имена классов: Tailwind вырезает из @layer components классы, собранные шаблонной строкой.
const STRIPE_CLASSES = { 'warning': 'ds-week-cell-stripe-warning', 'no-marks': 'ds-week-cell-stripe-no-marks' } as const
const CHIP_CLASSES: Record<ChipKind, string> = {
    violation: 'ds-week-chip-violation',
    waiting: 'ds-week-chip-waiting',
}

const { schedule, date } = defineProps<Props>()

const scheduleStore = useScheduleStore()
const departmentStore = useDepartmentStore()
const authStore = useAuthStore()

const day = computed(() => dayjs(date))
const isToday = computed(() => date === dayjs().format(DATE_FORMAT))
// Общий минутный тикер: «идёт», «Нет отметок» и будущая смена актуализируются без перезагрузки.
const now = useWallClockNow()

const notHired = computed(() => dayjs(schedule.hired_at).isAfter(day.value, 'day') || dayjs(schedule.fired_at).isBefore(day.value, 'day'))

const currentDepartmentId = computed(() => scheduleStore.currentDepartment?.id)
// RD-182: те же правила принадлежности, что в месяце: свои смены — ТУ строки в выбранном подразделении
// за точную дату. Внешняя занятость — только в строке собственного ТУ, отдельно от своих колонок и часов.
const shifts = computed(() => ownRowShifts(schedule, currentDepartmentId.value)
    .filter(shift => dayjs(shift.date).isSame(day.value, 'day')))
// По сценарию не больше одной внешней смены за день (spec r3 п.2): в ячейке — первая по плановому началу.
const externalView = computed(() => {
    const shift = rowExternalShifts(schedule, currentDepartmentId.value, date)[0]
    return shift && buildExternalShiftView(shift, departmentStore.departments, '')
})

const dayStateShift = computed(() => shifts.value.find(shift => Boolean(shift.day_state_document)))
const workShifts = computed(() => shifts.value
    .filter(shift => !shift.day_state_document && shift.plan_work_start_at && shift.plan_work_end_at)
    .sort((a, b) => Number(scheduleStore.isExtraShift(a)) - Number(scheduleStore.isExtraShift(b))))

const firstDayType = computed(() => scheduleStore.getDayType((dayStateShift.value ?? shifts.value[0])?.day_type ?? ''))

const mode = computed<CellMode>(() => {
    if (notHired.value) return 'blank'
    if (dayStateShift.value) return 'dayState'
    if (workShifts.value.length > 1) return 'multi'
    if (workShifts.value.length === 1) return 'single'
    // Только внешняя смена: информационный блок; редактор по-прежнему не открывается (ScheduleTable).
    if (externalView.value) return 'external'
    if (shifts.value.length) return 'dayoff'
    if (schedule.is_vacancy || schedule.department.id !== currentDepartmentId.value) return 'blank'
    // Как в месяце: «+» только для будущих дней, прошедшие и сегодняшний без смен — «Выходной».
    return day.value.isAfter(dayjs(), 'day') ? 'add' : 'dayoff'
})

const formatHours = (hours: number) => formatDecimalHours(hours, 'schedule')

function buildShiftView(shift: Shift) {
    const timing = describeShiftTiming(shift, now.value, shift.id ? scheduleStore.weekShiftHours.get(shift.id) : undefined)

    return {
        key: shift.id ?? `${shift.employment}-${shift.plan_work_start_at}`,
        shift,
        isExtra: Boolean(scheduleStore.isExtraShift(shift)),
        positionName: scheduleStore.getPosition(shift.position)?.name ?? schedule.position?.name ?? '',
        planStart: timing.planStart,
        planEnd: timing.planEnd,
        planNextDay: timing.planNextDay,
        factStart: timing.factStart,
        factEnd: timing.factEnd,
        factNextDay: timing.factNextDay,
        factHoursValue: timing.factHoursValue,
        factHours: timing.factHoursValue === null ? '' : formatHours(timing.factHoursValue),
        showPlanHours: !timing.isUpcoming,
        planHoursValue: timing.planHoursValue,
        planHours: formatHours(timing.planHoursValue),
        isInProgress: timing.isInProgress,
        isNoMarks: timing.isNoMarks,
        startViolated: timing.startViolated,
        endViolated: timing.endViolated,
        violationMessages: timing.violationMessages,
        violationCount: timing.violationCount,
        hasGeneralViolation: timing.hasGeneralViolation,
    }
}

const shiftViews = computed(() => workShifts.value.map(buildShiftView))
const singleShift = computed(() => mode.value === 'single' ? shiftViews.value[0] : undefined)
const visibleShifts = computed(() => shiftViews.value.slice(0, 2))
const hiddenShiftsCount = computed(() => Math.max(0, shiftViews.value.length - 2))

const totalFactHours = computed(() => {
    // Сумма только из значений табеля по каждой смене: неизвестное значение или идущая смена — суммы нет.
    const values = shiftViews.value.filter(view => view.showPlanHours).map(view => view.factHoursValue)
    if (!values.length || values.some(value => value === null)) return ''
    return formatHours(values.reduce<number>((sum, value) => sum + (value ?? 0), 0))
})
const totalPlanHours = computed(() => formatHours(shiftViews.value.reduce((sum, view) => sum + view.planHoursValue, 0)))

const isMixed = computed(() => Boolean(externalView.value && (mode.value === 'single' || mode.value === 'multi')))
// Своя + внешняя: видна собственная основная, остальные собственные — в лиловом счётчике; внешняя в него не входит.
const mixedOwnShifts = computed(() => shiftViews.value.slice(0, 1))
const mixedHiddenCount = computed(() => Math.max(0, shiftViews.value.length - 1))
// «из …» как у собственных смен без внешней: у одной будущей смены не показывается (как в single), у 2+ — как в multi.
const showTotalPlanHours = computed(() => mode.value === 'multi' || Boolean(shiftViews.value[0]?.showPlanHours))

const dayStateShiftView = computed(() => mode.value === 'dayState' && shiftViews.value.length ? shiftViews.value[0] : undefined)

const violationCount = computed(() => shiftViews.value.reduce((sum, view) => sum + view.violationCount, 0))

const stripe = computed<'warning' | 'no-marks' | undefined>(() => {
    // Статусы — только собственных смен: у дня только с внешней сменой полосы нет.
    if (mode.value !== 'single' && mode.value !== 'multi' && !dayStateShiftView.value) return undefined
    if (violationCount.value || shiftViews.value.some(view => view.hasGeneralViolation)) return 'warning'
    if (shiftViews.value.some(view => view.isNoMarks)) return 'no-marks'
    return undefined
})

const chips = computed(() => {
    const view = singleShift.value
    if (!view) return []
    const result: { text: string, kind: ChipKind }[] = []
    if (view.violationCount === 1) {
        result.push({ text: view.violationMessages[0] || 'Нарушение', kind: 'violation' })
    } else if (view.violationCount > 1) {
        result.push({ text: pluralViolations(view.violationCount), kind: 'violation' })
    }
    if (view.isNoMarks) result.push({ text: 'Нет отметок', kind: 'waiting' })
    if (view.isInProgress) result.push({ text: 'идёт', kind: 'waiting' })
    return result
})

// Кадровое состояние: название и заливка из справочника типа дня (DayType.color, палитра задаётся в админке).
// Непригодный или пустой цвет — базовый color-day-state-bg.
const dayStateName = computed(() => firstDayType.value?.name || 'Кадровое отклонение')
const dayStateColor = computed(() => {
    const color = firstDayType.value?.color?.trim()
    return color && CSS.supports('color', color) ? color : 'var(--color-day-state-bg)'
})

const dayoffLabel = computed(() => (shifts.value.length && firstDayType.value?.name) || 'Выходной')

// RD-185: готовое duration КС (Ч:ММ, неизвестное — «—») при DISPLAY_SHIFT_TIME_IN_CELL: true; иначе пусто
// и разметка КС прежняя. С часами и «из …» рабочих смен не складывается.
const dayStateDuration = computed(() => (
    dayStateShift.value && isDayStateDurationShown(authStore.settings) ? formatDayStateDuration(dayStateShift.value) : ''
))
</script>
